import React, { useContext, useEffect, useRef } from 'react';
import { NAVBAR_ELEMENT } from '../../../utils/constants';
import {
  BrFilter,
  ProductFilterInput,
  SearchProductsSortFragment,
} from '../../../../generated/product-service-graphql';
import { Sorting } from './Sorting';
import { useSearchParamHelpers } from '../../_shared/react-hooks';
import { SiteSettings } from '../../../utils/environment';
import { getSelectedFiltersCount } from './BloomreachFilters';
import { PlpContext } from '../PLPContext';
import { throttle } from 'lodash';
import sortDropdownStyles from '../../_shared/ui-components/YogaUI/alo-ui/AloDropdown/sorting.module.scss';
import { SORT_BY_KEY, getSortStyle } from './sorting-utils';

const StickyFiltersMenu = ({
  totalAppliedFilterCount,
  openPlpFiltersDrawer,
  isMobile,
  sortOptions,
  filters,
  selectedFilters,
}: {
  totalAppliedFilterCount: number;
  openPlpFiltersDrawer: () => void;
  isMobile: boolean;
  sortOptions: SearchProductsSortFragment[];
  filters: BrFilter[];
  selectedFilters: ProductFilterInput[];
}) => {
  // below is to move the navbar in and out of the viewport when scrolling
  const lastScrollTopRef = useRef<number>(0);
  const stickyFiltersMenuRef = useRef<HTMLDivElement>(null);
  const stickyUniBannerIsEnabled = SiteSettings?.uniBannerStickyEnable;

  const { searchParams } = useSearchParamHelpers(window.location.search, { replace: true });
  const initialOption = sortOptions.find((option) => option.value === searchParams.get(SORT_BY_KEY));

  // [LND-1005] the below is to handle the AC from UX to hide and show nav elements while the user scrolls up or down.
  useEffect(() => {
    const navbar = document.querySelector(NAVBAR_ELEMENT);
    const uniBanner = stickyUniBannerIsEnabled ? document.querySelector('.sticky-uni-banner') : null;
    const stickyFiltersMenu = stickyFiltersMenuRef.current;

    let ticking = false;

    const handleScroll = throttle(() => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const lastScrollTop = lastScrollTopRef.current;
          const currentScrollTop = window.scrollY || document.documentElement.scrollTop;

          if (!navbar || !stickyFiltersMenu) return;

          stickyFiltersMenu.style.transition = 'all 0.3s ease';

          const { height: uniBannerHeight = 0 } = uniBanner?.getBoundingClientRect() || {};
          const { height: mainNavHeight = 0 } = navbar?.getBoundingClientRect() || {};

          const headerHeight = Math.floor(mainNavHeight) + Math.floor(uniBannerHeight);

          if (currentScrollTop > lastScrollTop && currentScrollTop > headerHeight) {
            navbar.style.transition = 'transform 0.3s ease';
            navbar.style.transform = `translateY(-${headerHeight + 1}px)`;

            uniBanner && (uniBanner.style.transition = 'transform 0.3s ease');
            uniBanner && (uniBanner.style.transform = `translateY(-${headerHeight + 1}px)`);
            stickyFiltersMenu.style.top = `0px`;
          } else if (currentScrollTop < lastScrollTop) {
            navbar.style.transform = 'translateY(0)';
            uniBanner && (uniBanner.style.transform = 'translateY(0)');
            stickyFiltersMenu.style.top = `${headerHeight}px`;
          }

          lastScrollTopRef.current = currentScrollTop;
          ticking = false;
        });
        ticking = true;
      }
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [stickyUniBannerIsEnabled]);

  const sortValue = initialOption ? { key: initialOption.value, text: initialOption.label } : { key: 'SORT', text: '' };

  return (
    <div className={`sticky-filters-menu`} ref={stickyFiltersMenuRef}>
      <div className="pill-buttons">
        <button className="button-ghost-xs" onClick={openPlpFiltersDrawer}>
          All{!isMobile && ` Filters`}
          {totalAppliedFilterCount > 0 && ` (${totalAppliedFilterCount})`}
          <span className="alo-filters-icon"></span>
        </button>
        <QuickFilterPills
          filters={filters}
          selectedFilters={selectedFilters}
          openPlpFiltersDrawer={openPlpFiltersDrawer}
          isMobile={isMobile}
        />
      </div>
      {!isMobile ? (
        <Sorting
          value={sortValue}
          dropdownItems={sortOptions.map((sortOption) => ({
            key: sortOption.value,
            text: sortOption.label,
          }))}
          wrapperClass={sortDropdownStyles.sorting}
          MUIAutocompleteProps={{
            classes: getSortStyle(sortValue),
          }}
          labelPrefix="Sort by"
          hasBorder={false}
        />
      ) : null}
    </div>
  );
};

const FILTER_QUICK_PILLS_LIST = ['size', 'color', 'fabric'];

const QuickFilterPills = ({
  filters,
  selectedFilters,
  openPlpFiltersDrawer,
  isMobile,
}: {
  filters: BrFilter[];
  selectedFilters: ProductFilterInput[];
  openPlpFiltersDrawer: () => void;
  isMobile: boolean;
}) => {
  const { setActiveFilterAccordion } = useContext(PlpContext);

  const openDrawer = (id: string) => {
    setActiveFilterAccordion(id);
    openPlpFiltersDrawer();
  };

  return (
    <>
      {isMobile ? (
        <button key={'sort'} className="button-ghost-xs" onClick={() => openDrawer('sort')}>
          Sort
        </button>
      ) : null}
      {FILTER_QUICK_PILLS_LIST.map((pill) => filters.find((filter) => filter?.id?.toLowerCase() === pill)).map(
        (filter) => {
          if (!filter) return null;
          const pendingFiltersCount = getSelectedFiltersCount(filter, selectedFilters);
          return (
            <button key={filter.id} className="button-ghost-xs" onClick={() => openDrawer(filter.id)}>
              {pendingFiltersCount > 0 ? `${filter.label} (${pendingFiltersCount})` : filter.label}
            </button>
          );
        }
      )}
    </>
  );
};

export default StickyFiltersMenu;
